
import { IScheduleUpdate } from '@/interfaces/schedule';
import { adminStore, departureStore, mainStore, scheduleStore, userActionStore } from '@/store';
import { format, parse } from 'date-fns';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class ScheduleForm extends Vue {
  @Prop({ required: true, type: Object }) selectedEvent!: object;
  @Prop({ type: Boolean, default: false }) isLead?: boolean;
  // Data do componente
  id: number | null = null;
  name: string = '';
  description: string = '';
  responsibleUserId: number | null = null;
  isResponsibleReadonly = false;
  participantUserIds: number[] = [];
  link: string = '';
  departureId: number | null = null;
  date: string = '';
  statusId: number | null = null;
  loading: boolean = false;
  menuTime: Boolean = false;
  menuDate: Boolean = false;
  hora: string = '';
  horaFinal: string = '';
  dateFinal: string = '';
  menuTimeFinal: Boolean = false; // Novo menu para Data Final
  conflictDialog: boolean = false;
  conflictMessage: string = '';
  idFranchising = null;

  get formattedDate(): string {
    return this.date ? format(parse(this.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : '';
  }

  set formattedDate(value: string) {
    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
    this.date = format(parsedDate, 'yyyy-MM-dd');
  }

  // Mapeamento de status por departamento
  statusList = [
    { id: 1, color: 'primary', text: 'Agendado' },
    { id: 2, color: 'red', text: 'Realizado' },
    { id: 3, color: 'green', text: 'Não realizado' },
    { id: 4, color: 'yellow', text: 'Remarcado' },
  ];

  async mounted() {
    this.responsibleUserId = null;
    this.participantUserIds = [];
    if (this.selectedEvent) {
      this.id = this.selectedEvent.id;
      this.name = this.selectedEvent.name;
      this.description = this.selectedEvent.description;
      this.link = this.selectedEvent.link;
      this.statusId = this.selectedEvent.status_id;
      this.departureId = this.selectedEvent.user.departure_id;
      this.setDateTime();
      this.processScheduleParticipations(this.selectedEvent.schedule_participations);
    }

    this.idFranchising = this.currentUser?.franchising_id;

    const searchParams = {
      idFranchising: this.idFranchising,
      is_active: true,
    };

    await adminStore.getUsers({
      searchParams: searchParams,
    });
  }

  setDateTime() {
    const dateTime = this.selectedEvent.date;
    const dateTimeFinal = this.selectedEvent.date_final || null;

    // Separar data e hora inicial
    const [datePart, timePart] = dateTime.split('T');
    this.date = datePart;
    this.hora = timePart.substring(0, 5);

    // Preencher o campo horaFinal, se disponível
    if (dateTimeFinal) {
      const [, timePartFinal] = dateTimeFinal.split('T');
      this.horaFinal = timePartFinal.substring(0, 5);
    } else {
      this.horaFinal = '';
    }
  }

  get minDateTime() {
    const now = new Date();
    return format(now, "yyyy-MM-dd'T'HH:mm");
  }

  get departures() {
    return departureStore.departures;
  }

  @Watch('selectedEvent', { immediate: true, deep: true })
  onSelectedEventChanged() {
    this.responsibleUserId = null;
    this.participantUserIds = [];
    this.preencherInformacoesDoEvento();
  }

  get currentUser() {
    return mainStore.userProfile!;
  }

  get usersOptions() {
    return adminStore.users;
  }

  get responsibleOptions() {
    // Se o usuário não pode atribuir responsável e não tem role_id 1, ele próprio é o responsável
    if (this.currentUser?.role?.level !== 1 && !this.podeAtribuirResponsavel) {
      this.responsibleUserId = this.currentUser.id;
      this.isResponsibleReadonly = true;
      return [this.currentUser];
    } else {
      // Garante que usersOptions seja um array antes de filtrar
      const users = adminStore.usersFranchising;

      // Retorna apenas usuários do mesmo departamento
      return users.filter((user) => user.departure_id === this.currentUser.departure_id);
    }
  }

  // Método para enviar o formulário
  async submitForm() {
    this.loading = true;

    const dateHoraString = `${this.date} ${this.hora}:00`;
    const dateFinalString = `${this.date} ${this.horaFinal}:00`;

    // Verifica se já existe um evento marcado para o mesmo horário
    const hasConflict = await this.checkScheduleConflict(dateHoraString, dateFinalString);

    if (hasConflict) {
      this.conflictMessage = 'O responsável já tem um evento marcado nesse horário!';
      this.conflictDialog = true; // Exibe o diálogo de conflito
      this.loading = false;
      return;
    }

    const isConcluded = this.statusId === 2;

    const scheduleData: IScheduleUpdate = {
      id: this.id,
      name: this.name,
      description: this.description,
      link: this.link,
      departure_id: this.currentUser.departure_id,
      date: dateHoraString,
      date_final: dateFinalString,
      participants_user_id: this.participantUserIds,
      responsible_user_id: this.responsibleUserId,
      status_id: this.statusId,
      created_by_user_id: this.currentUser.id,
      is_concluded: isConcluded,
    };

    try {
      await scheduleStore.updateSchedule(scheduleData);
      this.$emit('carregarEventosNovosOuAtualizados');
      this.clearForm();
      this.closeDialog();
    } catch (error) {
      console.error('Erro ao criar evento', error);
    } finally {
      this.loading = false;
    }
  }

  clearForm() {
    this.name = '';
    this.description = '';
    this.responsibleUserId = null;
    this.participantUserIds = [];
    this.link = '';
    this.departureId = null;
    this.date = '';
    this.statusId = null;
  }

  get participantes() {
    return adminStore.users;
  }

  closeDialog() {
    this.$emit('close');
  }

  processScheduleParticipations(scheduleParticipations: { user_id: number; is_responsible: boolean }[]) {
    // Iterar pelo array e separar os valores
    scheduleParticipations.forEach((participation) => {
      if (participation.is_responsible) {
        this.responsibleUserId = participation.user_id;
      } else {
        this.participantUserIds.push(participation.user_id);
      }
    });
  }

  get participantOptions() {
    if (!Array.isArray(this.usersOptions)) {
      return []; // Retorna um array vazio se `usersOptions` não for um array
    }

    const selectedParticipants = this.usersOptions.filter((user) => this.participantUserIds.includes(user.id));
    const unselectedParticipants = this.usersOptions.filter((user) => !this.participantUserIds.includes(user.id));

    return [...selectedParticipants, ...unselectedParticipants];
  }
  preencherInformacoesDoEvento() {
    this.id = this.selectedEvent.id;
    this.name = this.selectedEvent.name;
    this.description = this.selectedEvent.description;
    this.link = this.selectedEvent.link;
    this.statusId = this.selectedEvent.status_id;
    this.departureId = this.selectedEvent.user.departure_id;
    this.setDateTime();
    this.processScheduleParticipations(this.selectedEvent.schedule_participations);
  }

  onStatusChange(newStatus) {
    this.statusId = newStatus;
  }

  async checkScheduleConflict(startDate: string, endDate: string | null) {
    // Buscar eventos já agendados do responsável
    const events = await scheduleStore.getSchedulesByUser(this.responsibleUserId);

    // Filtra os eventos para excluir o evento atual (o que está sendo editado)
    const filteredEvents = events.filter((event) => event.id !== this.id);

    return filteredEvents.some((event) => {
      const eventStart = new Date(event.date);
      const eventEnd = event.date_final ? new Date(event.date_final) : eventStart;

      // Verifica se há sobreposição de horários
      return (
        new Date(startDate) < eventEnd && (endDate ? new Date(endDate) > eventStart : new Date(startDate) >= eventStart)
      );
    });
  }

  get podeAtribuirResponsavel() {
    return userActionStore.AtribuirResponsavelSchedule;
  }
}
